import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { Sar } from '../types/sars';

/**
 * Suspicious Activity Reports (manager / super_admin). Draft from a subject,
 * then a DIFFERENT actor files (with the regulator reference) or rejects —
 * four-eyes on the file. Append-only / WORM on the backend.
 */
class SarsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async list(): Promise<Sar[]> {
    const res = await this.api.get<Sar[]>('/api/v1/compliance/sars');
    return res.data;
  }

  /** Draft a SAR for a subject — the payload is auto-assembled server-side from
   *  the subject's risk band, monitoring alerts, sanctions hits and activity. */
  async draft(
    subjectUserId: string, reason: string, narrative?: string,
  ): Promise<{ id: string; status: string }> {
    const res = await this.api.post<{ id: string; status: string }>(
      '/api/v1/compliance/sars',
      { subject_user_id: subjectUserId, reason, narrative },
    );
    return res.data;
  }

  /** File a drafted SAR (four-eyes: filer ≠ drafter). */
  async file(sarId: string, externalRef: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/compliance/sars/${sarId}/file`, {
      external_ref: externalRef,
      reason,
    });
  }

  async reject(sarId: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/compliance/sars/${sarId}/reject`, { reason });
  }

  async openCount(): Promise<number> {
    return (await this.list()).filter((s) => s.status === 'draft').length;
  }
}

export default new SarsService();
